import React from 'react';
import { Entry } from 'contentful';

import {
  TypeCollectionPage,
  TypeProductPage,
  TypeProductPageFields,
} from '@/core/generated-types';
import { CollectionProduct } from '@/components/ui/CollectionProduct';
import { useYotpoScript } from '@/hooks/useYotpoScript';
import { ShopifyProduct } from '@/lib/shopify';
import { Seo } from '@/components/ui/Seo';
import {
  ExtendedProductFields,
  buildCanonicalUrl,
} from '@/core/contentfulConfig';

import { orderedPages } from './utils';
import { DynamicComponent } from '@/components/common';
import { ShopAllBanner } from '@/components/subflow-v3/components/ShopAllBanner';
export interface CollectionPageLayoutProps {
  page: TypeCollectionPage;
  shopifyProducts: (ShopifyProduct | null)[];
}

interface TypeProductPageFieldsExtended
  extends Omit<TypeProductPageFields, 'product'> {
  product: Entry<ExtendedProductFields>;
}

export const CollectionPageLayout: React.FC<CollectionPageLayoutProps> = ({
  page,
}) => {
  const {
    fields: {
      collectionName,
      pageMetafields,
      productPages,
      marketingPages,
      buttonText = 'View Product',
      headerImage,
      bannerTitle,
      bannerText,
      mobileHeaderImage,
      bannerCTA,
      hideGradient,
      textColor = 'light',
    },
  } = page;
  const metadataFields = pageMetafields?.fields;
  const pages = marketingPages
    ? orderedPages(productPages, marketingPages)
    : productPages;

  useYotpoScript();

  return (
    <>
      <Seo
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow}
        noindex={metadataFields?.noIndex}
        title={metadataFields?.pageTitle}
        canonical={buildCanonicalUrl(page)}
      />
      {/* Hero */}

      <ShopAllBanner
        bannerCTA={bannerCTA}
        bannerTitle={bannerTitle}
        bannerText={bannerText}
        collectionName={collectionName}
        headerImage={headerImage}
        mobileHeaderImage={mobileHeaderImage}
        hideGradient={hideGradient}
        textColor={textColor}
      />

      {/* Products */}
      <div className="mx-auto grid grid-cols-1 gap-3 px-4 md:max-w-[748px] md:grid-cols-2 lg:max-w-[1128px] lg:grid-cols-3 lg:gap-6 lg:px-4">
        {pages.map((page) => {
          if (page.sys.contentType.sys.id === 'collectionMarketingCard') {
            return (
              <DynamicComponent key={page.fields.entryName} component={page} />
            );
          }

          const shopifyProduct = (page.fields as TypeProductPageFieldsExtended)
            ?.product?.fields?.shopifyProduct;

          return (
            shopifyProduct && (
              <CollectionProduct
                key={page.fields.entryName}
                productPage={page as unknown as TypeProductPage}
                shopifyProduct={shopifyProduct}
                buttonText={buttonText}
              />
            )
          );
        })}
      </div>
    </>
  );
};
